import { useCallback, useRef } from 'react'
import { useAppStore } from '../store/appStore'
import { fetchWikiPOIs, fetchWikiExtract, fetchWikiExtractByTitleGuess } from '../services/api/wikipedia'
import { fetchOSMOverpass } from '../services/api/osm'
import { fetchWikidataDesc, fetchWikidataAroundNature } from '../services/api/wikidata'
import { fetchGooglePlaces } from '../services/api/googlePlaces'
import { fetchJsonRobust } from '../services/api/fetch'
import { buildSpeech, speakOnce } from '../services/speech'
import {
  USE_WIKIPEDIA,
  USE_OSM_WIKIDATA,
  USE_WIKIDATA_NATURE,
  RADIO_MIN_INTERVAL_MS,
  SPOKEN_CACHE_TTL_MS,
  REJECT_CACHE_TTL_MS,
  POST_SPEECH_COOLDOWN_MS,
  AHEAD_CONE_DEG,
  MIN_MOVE_M,
  MAX_SPOKEN_CHARS,
} from '../utils/constants'
import { pruneCache } from '../utils/cache'
import { haversineMeters, bearingDeg, clampAngleDeltaDeg, now } from '../utils/geo'
import { wikiDomainFor, titleLooksUninteresting, trimToSentence } from '../utils/text'
import { NATURE_KEYWORDS } from '../utils/constants'
import type { POICandidate, Position } from '../types'

interface CacheEntry {
  timestamp: number
}

function isNatureBiased(c: POICandidate): boolean {
  if (c.source === 'wdnature') return true
  if (c.source === 'osm') {
    const tags = c.osm?.tags || {}
    if (tags.natural || tags.landuse === 'forest' || tags.boundary === 'protected_area') return true
    if (tags.waterway || tags.wetland) return true
  }
  const title = (c.title || '').toLowerCase()
  return NATURE_KEYWORDS.some((w) => title.includes(w))
}

function scoreCandidate(c: POICandidate): number {
  let s = (c.dist || 0) + (c.deltaAbs ?? 0) * 15
  if (isNatureBiased(c)) s *= 0.75
  if (c.source === 'wiki') s *= 0.9
  if (c.source === 'osm') s *= 0.95
  if (c.source === 'wdnature') s *= 0.92
  return s
}

function rankCandidates(
  cands: POICandidate[],
  lastPos: Position | null,
  spokenCache: Map<string, CacheEntry>,
  rejectCache: Map<string, CacheEntry>
): POICandidate[] {
  pruneCache(spokenCache, SPOKEN_CACHE_TTL_MS)
  pruneCache(rejectCache, REJECT_CACHE_TTL_MS)

  const h = lastPos ? lastPos.heading : null

  const withGeo = cands
    .filter((c) => !spokenCache.has(c.key))
    .filter((c) => !rejectCache.has(c.key))
    .filter((c) => !titleLooksUninteresting(c.title))
    .map((c) => {
      if (!lastPos) return c
      const dist = haversineMeters(lastPos.lat, lastPos.lon, c.lat, c.lon)
      const b = bearingDeg(lastPos.lat, lastPos.lon, c.lat, c.lon)
      const deltaSigned = clampAngleDeltaDeg(b - (h ?? 0))
      const deltaAbs = Math.abs(deltaSigned)
      return { ...c, dist, bearing: b, deltaSigned, deltaAbs }
    })

  let pool = withGeo
  if (h != null) {
    const ahead = withGeo.filter((c) => (c.deltaAbs ?? 0) <= AHEAD_CONE_DEG)
    pool = ahead.length ? ahead : withGeo
  }

  pool.sort((a, b) => scoreCandidate(a) - scoreCandidate(b))
  return pool
}

export function usePOIDetection() {
  const store = useAppStore()
  const spokenCacheRef = useRef<Map<string, CacheEntry>>(new Map())
  const rejectCacheRef = useRef<Map<string, CacheEntry>>(new Map())
  const narrateInFlightRef = useRef(false)
  const lastQueryAtRef = useRef(0)
  const lastSpokenStartedAtRef = useRef(0)
  const lastSpokenEndedAtRef = useRef(0)
  const hardLockedUntilRef = useRef(0)
  const speakingRef = useRef(false)
  const pendingSpeechRef = useRef(false)
  const prevPosRef = useRef<Position | null>(null)

  const isLocked = useCallback((): boolean => {
    return (
      speakingRef.current ||
      pendingSpeechRef.current ||
      narrateInFlightRef.current ||
      now() < hardLockedUntilRef.current
    )
  }, [])

  const hardLock = useCallback((ms: number = 900) => {
    hardLockedUntilRef.current = Math.max(hardLockedUntilRef.current, now() + ms)
  }, [])

  const narrateOnce = useCallback(async () => {
    const lastPos = store.getPosition()
    if (!lastPos) return
    if (isLocked()) return

    const t = now()
    // Throttle POI detection to once every 10 seconds to avoid API overload
    if (t - lastQueryAtRef.current < 10000) return
    if (t - lastSpokenEndedAtRef.current < POST_SPEECH_COOLDOWN_MS) return

    const lang = store.language
    const radius = store.settings.radius
    const radio = store.settings.radio

    if (radio && t - lastSpokenStartedAtRef.current < RADIO_MIN_INTERVAL_MS) {
      return
    }

    narrateInFlightRef.current = true
    lastQueryAtRef.current = t
    store.setIsFetchingPOIs(true)

    try {
      const candidates: POICandidate[] = []

      // Fetch from all sources in parallel for speed
      const [wikiResults, osmResults, wdnResults, googleResults] = await Promise.allSettled([
        // Wikipedia
        USE_WIKIPEDIA
          ? (async () => {
              const domains = [wikiDomainFor(lang)]
              if (lang !== 'en') domains.push('en.wikipedia.org')
              for (const dom of domains) {
                try {
                  const hits = await fetchWikiPOIs(dom, lastPos.lat, lastPos.lon, radius)
                  const results = hits
                    .filter((h) => !titleLooksUninteresting(h.title))
                    .map((h) => ({
                      source: 'wiki' as const,
                      key: `w:${h.pageid}`,
                      domain: dom,
                      pageid: h.pageid,
                      title: h.title,
                      lat: h.lat,
                      lon: h.lon,
                    }))
                  if (results.length) return results
                } catch (e) {
                  // Continue to next domain
                }
              }
              return []
            })()
          : Promise.resolve([]),
        // OSM
        USE_OSM_WIKIDATA
          ? fetchOSMOverpass(lastPos.lat, lastPos.lon, radius)
              .then((osm) =>
                osm.map((o) => ({
                  source: 'osm' as const,
                  key: `osm:${o.osmType}:${o.id}`,
                  osm: o,
                  title: o.name,
                  lat: o.lat,
                  lon: o.lon,
                }))
              )
              .catch(() => [])
          : Promise.resolve([]),
        // Wikidata Nature
        USE_WIKIDATA_NATURE
          ? fetchWikidataAroundNature(lastPos.lat, lastPos.lon, radius, lang).catch(() => [])
          : Promise.resolve([]),
        // Google Places
        store.settings.useGoogle
          ? fetchGooglePlaces(lastPos.lat, lastPos.lon, radius)
              .then((places) =>
                places.map((p) => ({
                  source: 'gplaces' as const,
                  key: `g:${p.id}`,
                  g: p,
                  title: p.name,
                  lat: p.lat,
                  lon: p.lon,
                  rating: p.rating,
                  userRatings: p.userRatingsTotal,
                  types: p.types || [],
                }))
              )
              .catch(() => [])
          : Promise.resolve([]),
      ])

      // Collect all candidates
      if (wikiResults.status === 'fulfilled') {
        candidates.push(...wikiResults.value)
      }
      if (osmResults.status === 'fulfilled') {
        candidates.push(...osmResults.value)
      }
      if (wdnResults.status === 'fulfilled') {
        candidates.push(...wdnResults.value)
      }
      if (googleResults.status === 'fulfilled') {
        candidates.push(...googleResults.value)
      }

      // Rank candidates
      const ranked = rankCandidates(
        candidates,
        lastPos,
        spokenCacheRef.current,
        rejectCacheRef.current
      )

      // Always update nearby POIs in store (even if empty)
      store.setNearbyPOIs(ranked.slice(0, 10))

      if (!ranked.length) return

      for (let attempt = 0; attempt < Math.min(6, ranked.length); attempt++) {
        const best = ranked[attempt]
        hardLock(1400)

        let title = best.title
        let extract = ''
        let extraPrefix = ''

        try {
          if (best.source === 'wiki') {
            const ext = await fetchWikiExtract(best.domain!, best.pageid!)
            if (!ext || !ext.extract) throw new Error('Empty wiki extract')
            title = ext.title
            extract = ext.extract
          } else if (best.source === 'osm') {
            const o = best.osm!
            if (o.wikipedia) {
              const parts = o.wikipedia.split(':')
              const wlang = parts[0] || lang
              const pageTitle = parts.slice(1).join(':')
              const dom = wikiDomainFor(wlang)
              const url = `https://${dom}/w/api.php?action=query&prop=extracts&exintro=1&explaintext=1&redirects=1&titles=${encodeURIComponent(pageTitle)}&format=json&origin=*`
              const data = await fetchJsonRobust(url)
              const pages = data?.query?.pages
              const firstKey = pages ? Object.keys(pages)[0] : null
              const page = firstKey ? pages[firstKey] : null
              if (page && page.extract) {
                title = page.title || title
                extract = page.extract
              } else if (o.wikidata) {
                const wd = await fetchWikidataDesc(o.wikidata, lang)
                if (wd) {
                  title = wd.title
                  extract = wd.extract
                }
              }
            } else if (o.wikidata) {
              const wd = await fetchWikidataDesc(o.wikidata, lang)
              if (wd) {
                title = wd.title
                extract = wd.extract
              }
            }
            if (!extract) {
              const tags = o.tags || {}
              const kind =
                tags.natural ||
                tags.historic ||
                tags.tourism ||
                tags.waterway ||
                tags.landuse ||
                tags.boundary ||
                'plek'
              extract =
                lang === 'nl'
                  ? `Dit is een ${kind} in deze omgeving.`
                  : `This is a ${kind} in this area.`
            }
          } else if (best.source === 'wdnature') {
            extract = best.desc || ''
            if (!extract) throw new Error('Empty wikidata desc')
          } else if (best.source === 'gplaces') {
            const r =
              typeof best.rating === 'number' ? best.rating.toFixed(1) : null
            const n =
              typeof best.userRatings === 'number' ? best.userRatings : null
            if (lang === 'nl' && r && n) {
              extraPrefix = `Google beoordeling ${r} op basis van ${n} reviews.`
            } else if (r && n) {
              extraPrefix = `Google rating ${r} based on ${n} reviews.`
            }

            const wikiHit = await fetchWikiExtractByTitleGuess(lang, best.title)
            if (!wikiHit || !wikiHit.extract) {
              throw new Error('No wikipedia enrichment for place')
            }
            title = wikiHit.title
            extract = wikiHit.extract
          }

          if (titleLooksUninteresting(title)) throw new Error('Filtered title')
          const cleaned = trimToSentence(extract, MAX_SPOKEN_CHARS)
          if (!cleaned || cleaned.length < 60) throw new Error('Text too short')

          const speech = buildSpeech(
            title,
            extract,
            {
              headingKnown: lastPos.heading != null,
              deltaSigned: best.deltaSigned || 0,
              deltaAbs: best.deltaAbs || 0,
            },
            lang,
            extraPrefix
          )

          spokenCacheRef.current.set(best.key, { timestamp: now() })
          store.setLastPOI(title)
          store.setPoiPosition({
            lat: best.lat,
            lon: best.lon,
            title,
          })
          store.addPOIHistory(best, speech)

          pendingSpeechRef.current = true
          speakingRef.current = false
          hardLock(1200)

          try {
            window.speechSynthesis.cancel()
          } catch (_) {}

          speakOnce(
            speech,
            lang,
            () => {
              pendingSpeechRef.current = false
              speakingRef.current = true
              store.setStatus('Spreekt…')
              lastSpokenStartedAtRef.current = now()
            },
            () => {
              pendingSpeechRef.current = false
              speakingRef.current = false
              store.setStatus('Luistert (GPS)…')
              lastSpokenEndedAtRef.current = now()
              hardLockedUntilRef.current = Math.max(
                hardLockedUntilRef.current,
                now() + POST_SPEECH_COOLDOWN_MS
              )
            },
            () => {
              pendingSpeechRef.current = false
              speakingRef.current = false
              store.setStatus('Luistert (GPS)…')
              hardLockedUntilRef.current = Math.max(
                hardLockedUntilRef.current,
                now() + 800
              )
            }
          )

          store.setLastSpoken(speech)
          return
        } catch (e) {
          rejectCacheRef.current.set(best.key, { timestamp: now() })
          hardLockedUntilRef.current = Math.min(
            hardLockedUntilRef.current,
            now() + 120
          )
          continue
        }
      }
    } finally {
      narrateInFlightRef.current = false
      store.setIsFetchingPOIs(false)
    }
  }, [store, isLocked, hardLock])

  const handlePositionUpdate = useCallback(
    (position: Position, accuracy: number) => {
      store.setPosition(position)
      store.setLastLocation(
        `${position.lat.toFixed(5)}, ${position.lon.toFixed(5)} (±${Math.round(accuracy)}m)`
      )
      store.setHeading(position.heading)

      if (isLocked()) return

      if (prevPosRef.current && !store.settings.radio) {
        const moved = haversineMeters(
          prevPosRef.current.lat,
          prevPosRef.current.lon,
          position.lat,
          position.lon
        )
        if (moved < MIN_MOVE_M) return
      }

      prevPosRef.current = position
      store.setStatus('Luistert (GPS)…')
      
      // Only call narrateOnce if auto-update is enabled or if we don't have POIs yet
      // This prevents clearing POIs on every position update
      if (store.settings.autoUpdate || store.nearbyPOIs.length === 0) {
        narrateOnce()
      }
    },
    [store, isLocked, narrateOnce]
  )

  const narratePOI = useCallback(async (poi: POICandidate) => {
    // Set loading state
    store.setNarratingPOI(poi.key)
    store.setNarrationError(null)
    store.setStatus('Ophalen informatie...')

    const lastPos = store.getPosition()
    if (!lastPos) {
      store.setNarrationError('No position available')
      store.setNarratingPOI(null)
      return
    }

    const lang = store.language
    let title = poi.title
    let extract = ''
    let extraPrefix = ''

    try {
      if (poi.source === 'wiki' && poi.domain && poi.pageid) {
        const ext = await fetchWikiExtract(poi.domain, poi.pageid)
        if (!ext || !ext.extract) throw new Error('Empty wiki extract')
        title = ext.title
        extract = ext.extract
      } else if (poi.source === 'osm' && poi.osm) {
        const o = poi.osm
        if (o.wikipedia) {
          const parts = o.wikipedia.split(':')
          const wlang = parts[0] || lang
          const pageTitle = parts.slice(1).join(':')
          const dom = wikiDomainFor(wlang)
          const url = `https://${dom}/w/api.php?action=query&prop=extracts&exintro=1&explaintext=1&redirects=1&titles=${encodeURIComponent(pageTitle)}&format=json&origin=*`
          const data = await fetchJsonRobust(url)
          const pages = data?.query?.pages
          const firstKey = pages ? Object.keys(pages)[0] : null
          const page = firstKey ? pages[firstKey] : null
          if (page && page.extract) {
            title = page.title || title
            extract = page.extract
          } else if (o.wikidata) {
            const wd = await fetchWikidataDesc(o.wikidata, lang)
            if (wd) {
              title = wd.title
              extract = wd.extract
            }
          }
        } else if (o.wikidata) {
          const wd = await fetchWikidataDesc(o.wikidata, lang)
          if (wd) {
            title = wd.title
            extract = wd.extract
          }
        }
        if (!extract) {
          const tags = o.tags || {}
          const kind =
            tags.natural ||
            tags.historic ||
            tags.tourism ||
            tags.waterway ||
            tags.landuse ||
            tags.boundary ||
            'plek'
          extract =
            lang === 'nl'
              ? `Dit is een ${kind} in deze omgeving.`
              : `This is a ${kind} in this area.`
        }
      } else if (poi.source === 'wdnature') {
        extract = poi.desc || ''
        if (!extract) throw new Error('Empty wikidata desc')
      } else if (poi.source === 'gplaces') {
        const r = typeof poi.rating === 'number' ? poi.rating.toFixed(1) : null
        const n = typeof poi.userRatings === 'number' ? poi.userRatings : null
        if (lang === 'nl' && r && n) {
          extraPrefix = `Google beoordeling ${r} op basis van ${n} reviews.`
        } else if (r && n) {
          extraPrefix = `Google rating ${r} based on ${n} reviews.`
        }

        // Try multiple sources for Google Places
        let wikiHit = await fetchWikiExtractByTitleGuess(lang, poi.title)
        
        // Fallback 1: Try Wikipedia by title guess
        if (!wikiHit || !wikiHit.extract) {
          wikiHit = await fetchWikiExtractByTitleGuess(lang, poi.title).catch(() => null)
        }
        
        // Fallback 2: Try Wikidata by searching for the title
        if (!wikiHit || !wikiHit.extract) {
          // Try to find Wikidata entity and get description
          const wikidataResults = await fetch(
            `https://www.wikidata.org/w/api.php?action=wbsearchentities&search=${encodeURIComponent(poi.title)}&language=${lang}&limit=1&format=json&origin=*`
          ).then(r => r.json()).catch(() => null)
          
          if (wikidataResults?.search?.[0]?.id) {
            const wikidataDesc = await fetchWikidataDesc(wikidataResults.search[0].id, lang)
            if (wikidataDesc) {
              title = wikidataDesc.title
              extract = wikidataDesc.extract
            }
          }
        }
        
        // Fallback 3: Use basic information if no detailed description found
        if (!extract || extract.length < 60) {
          extract = lang === 'nl' 
            ? `${poi.title} is een interessante locatie in deze omgeving.${r ? ` Het heeft een Google-beoordeling van ${r} sterren.` : ''}`
            : `${poi.title} is an interesting location in this area.${r ? ` It has a Google rating of ${r} stars.` : ''}`
        } else {
          title = wikiHit!.title
          extract = wikiHit!.extract
        }
      }

      if (titleLooksUninteresting(title)) throw new Error('Filtered title')
      const cleaned = trimToSentence(extract, MAX_SPOKEN_CHARS)
      if (!cleaned || cleaned.length < 60) throw new Error('Text too short')

      const bearing = bearingDeg(lastPos.lat, lastPos.lon, poi.lat, poi.lon)
      const heading = lastPos.heading
      const deltaSigned = heading != null ? clampAngleDeltaDeg(bearing - heading) : 0
      const deltaAbs = Math.abs(deltaSigned)

      const speech = buildSpeech(
        title,
        extract,
        {
          headingKnown: heading != null,
          deltaSigned,
          deltaAbs,
        },
        lang,
        extraPrefix
      )

      store.setLastPOI(title)
      store.setPoiPosition({
        lat: poi.lat,
        lon: poi.lon,
        title,
      })
      store.addPOIHistory(poi, speech)

      try {
        window.speechSynthesis.cancel()
      } catch (_) {}

      speakOnce(
        speech,
        lang,
        () => {
          store.setStatus('Spreekt…')
          store.setNarratingPOI(null) // Clear loading once speech starts
        },
        () => {
          store.setStatus('Luistert (GPS)…')
        },
        () => {
          store.setStatus('Luistert (GPS)…')
        }
      )

      store.setLastSpoken(speech)
    } catch (e) {
      console.warn('Error narrating POI:', e)
      const errorMsg = e instanceof Error ? e.message : 'Fout bij voorlezen'
      store.setStatus('Fout bij voorlezen')
      store.setNarrationError(errorMsg)
      store.setNarratingPOI(null)
      
      // Clear error after 3 seconds
      setTimeout(() => {
        store.setNarrationError(null)
      }, 3000)
    }
  }, [store])

  return { handlePositionUpdate, narrateOnce, narratePOI }
}
